import { useMock } from './utils';

const connectURL = window.location.origin;

/**
 * Thrown when the Connect authentication endpoint returns a non-2xx response.
 * Carrying the HTTP status as a typed property allows callers to distinguish
 * token rejection errors (401/403) from other failures (network, 5xx) without
 * relying on fragile string matching against the error message.
 */
export class AuthenticationError extends Error {
  constructor(public readonly status: number) {
    super(`Authentication failed with status ${status}`);
    this.name = 'AuthenticationError';
  }
}

export async function authenticateToConnect({
  email,
  password,
  turnstileToken,
}: {
  email: string;
  password: string;
  turnstileToken: string;
}) {
  if (!useMock) {
    const response = await fetch(`${connectURL}/api/authentication.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user: email,
        password,
        persistentLogin: false,
        turnstileToken,
      }),
    });

    if (!response.ok) {
      throw new AuthenticationError(response.status);
    }
  } else {
    console.log('Authenticate on Connect');
  }
}

/**
 * Probe whether the freshly created venue's extranet settings are queryable yet.
 * Connect's bootstrap hits this same endpoint on login; a 200 means the new
 * venue's authorisation has propagated and Connect will load rather than showing
 * a 403 blank page (CPG-176). Any non-2xx (typically 403/500 during the brief
 * post-provisioning window) — or a network error — means "not ready yet".
 * Must be called after authenticateToConnect so the session cookie is sent.
 */
export async function isConnectVenueReady(): Promise<boolean> {
  if (useMock) {
    return true;
  }
  try {
    const response = await fetch(`${connectURL}/api/extranet-settings.json`, {
      method: 'GET',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}
